/* tslint:disable:no-access-missing-member */
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';

import {
    INDDIntlFormat,
    NDDIntlService,
} from 'ndd-ng-intl';

import { NDDFormErrorValidatorService } from '../ndd-ng-form-error/shared/ndd-ng-form-error-validator.service';
import { NDDFormErrorService } from '../ndd-ng-form-error/shared/ndd-ng-form-error.service';
import { NDDFormsBaseComponent } from '../shared/ndd-ng-forms.base-component';
import { INDDFormNumericFormattedValue } from './shared/ndd-ng-form-numeric.model';

/** Definições para evento de seleção do input. */
type NDDFormNumberInputSelection = { start: number, end: number, text: string };

/** Componente numérico. */
@Component({
    selector: 'ndd-ng-form-numeric',
    template: require('./ndd-ng-form-numeric.component.html'),
})
export class NDDFormNumericComponent extends NDDFormsBaseComponent implements OnInit {
    private static CHAR_CODE_MIN_NUMERIC: number = 48;
    private static CHAR_CODE_MAX_NUMERIC: number = 57;
    private static MAX_DIGITS_ALLOWED: number = 16;

    /** Padrão de formatação do valor. */
    @Input() public controlFormat?: INDDIntlFormat;
    /** Define número máximo inteiro. */
    @Input() public controlIntegerMax: number = Number.MAX_SAFE_INTEGER;
    /** Define número mínimo inteiro. */
    @Input() public controlIntegerMin: number = Number.MIN_SAFE_INTEGER;
    /** Define número máximo de dígitos decimais. */
    @Input() public controlDecimalDigitsMax: number = 0;
    /** Define número mínimo de dígitos decimais. */
    @Input() public controlDecimalDigitsMin?: number;
    /** Exibir botões spin. */
    @Input() public controlShowSpinButtons: boolean = true;
    /** Medida de adição/subtração para o valor. */
    @Input() public controlStep: number = 1;
    /** Prefixo para o controle. */
    @Input() public controlPrefix?: string;
    /** Sufixo para o controle. */
    @Input() public controlPostfix?: string;
    /** Define controle como somente leitura. */
    @Input() public controlReadonly?: boolean;

    /** Evento de alteração do valor formatado. */
    @Output() public onControlFormattedValueChange: EventEmitter<INDDFormNumericFormattedValue> = new EventEmitter();

    @ViewChild('input', { static: false }) public input: ElementRef;

    /** Valor com formatação local para decimais. */
    public get localeValue(): string {
        const containsDecimals: boolean = this.controlValueString.indexOf('.') > -1;

        if (this.controlValueAux || this.controlValueAux === 0) {
            if (!containsDecimals
                && !this.finalZeros
                && this.control.value !== null
                && this.control.value.toString().substr(-1) === this.localeDecimalChar) {
                // Mantém vírgula no final do valor, caso o valor do controle contenha vírgula no final.
                return this.controlValueString + this.localeDecimalChar;
            } else if (!containsDecimals && this.finalZeros) {
                // Mantém vírgula e zeros decimais no final do valor, caso o valor do controle contenha vírgula e zeros decimais no final.
                return this.controlValueString + this.localeDecimalChar + this.finalZeros;
            }

            // Substitui símbolo decimal e mantém zeros decimais no final do valor, caso o valor do controle contenha zeros decimais no final.
            return this.controlValueString.replace('.', this.localeDecimalChar) + this.finalZeros;
        }

        return this.controlValueString;
    }
    /** Define se valor a ser exibido deve usar formatação. */
    public isFormattedValue: boolean = true;

    /** Caractere decimal local. */
    private get localeDecimalChar(): string {
        if (this.controlFormat && this.controlFormat.culture) {
            return this.intl.numberSymbols(this.controlFormat.culture).decimal;
        }

        return this.intl.numberSymbols().decimal;
    }

    /** Caractere milhar local. */
    private get localeGroupChar(): string {
        if (this.controlFormat && this.controlFormat.culture) {
            return this.intl.numberSymbols(this.controlFormat.culture).group;
        }

        return this.intl.numberSymbols().group;
    }

    /** Caractere negativo local. */
    private get localeMinusSign(): string {
        if (this.controlFormat && this.controlFormat.culture) {
            return this.intl.numberSymbols(this.controlFormat.culture).minusSign;
        }

        return this.intl.numberSymbols().minusSign;
    }

    private get toFixedValue(): number {
        return this.controlDecimalDigitsMax || this.controlDecimalDigitsMax === 0
            ? this.controlDecimalDigitsMax
            : NDDFormNumericComponent.MAX_DIGITS_ALLOWED - this.controlValueAux.toString().replace('.', '').replace('-', '').length;
    }

    private get controlValueString(): string {
        if (this.controlValueAux === null || this.controlValueAux === undefined) { return ''; }

        return this.controlValueAux.toString();
    }

    private controlValueAux: any;
    private inputSelection: NDDFormNumberInputSelection;
    private finalZeros: string = '';

    constructor(
        @Inject(Window) private window: Window,
        private intl: NDDIntlService,
        formErrorService: NDDFormErrorService,
        cdr: ChangeDetectorRef) {
        super(formErrorService, cdr);
    }

    public ngOnInit(): void {
        // Define variável auxiliar para evitar conflitos provenientes de two-way binding na propriedade `controlValue`.
        this.controlValueAux = this.controlValue;
        this.controlValueAux = this.controlValueAux || this.controlValueAux === 0 ? this.controlValueAux : null;
        // Define formatação com base no número máximo de dígitos decimas, caso não tenha sido definido.
        this.controlFormat = this.controlFormat ? this.controlFormat : { format: `n${ this.controlDecimalDigitsMax || '' }` };

        super.addControl();

        if (this.controlIntegerMax || this.controlIntegerMax === 0) {
            super.cacheValidator(NDDFormErrorValidatorService.max(this.controlIntegerMax, this.localeGroupChar, this.localeDecimalChar));
        }

        if (this.controlIntegerMin || this.controlIntegerMin === 0) {
            super.cacheValidator(NDDFormErrorValidatorService.min(this.controlIntegerMin, this.localeGroupChar, this.localeDecimalChar));
        }

        if (this.controlDecimalDigitsMax || this.controlDecimalDigitsMax === 0) {
            super.cacheValidator(NDDFormErrorValidatorService.maxDecimalDigits(this.controlDecimalDigitsMax, this.localeDecimalChar));
        }

        if (this.controlDecimalDigitsMin) {
            super.cacheValidator(NDDFormErrorValidatorService.minDecimalDigits(this.controlDecimalDigitsMin, this.localeDecimalChar));
        }

        super.addValidators();

        this.control.valueChanges.pipe(
            takeUntil(this.ngUnsubscribe))
            .subscribe(this.handleValueChanges.bind(this));
    }

    /**
     * Evento `focus`.
     * Mostra valor de edição (sem formatação).
     *
     * @memberof NDDFormNumericComponent
     */
    public hideFormattedValue(input: HTMLInputElement): void {
        this.isFormattedValue = false;

        this.window.setTimeout(() => {
            // Seleciona todo texto do input.
            input.setSelectionRange(0, input.value.length);
        });
    }

    /**
     * Evento `focusout`.
     * Mostra valor de exibição (com formatação).
     *
     * @memberof NDDFormNumericComponent
     */
    public showFormattedValue(): void {
        this.finalZeros = '';
        this.control.setValue(this.controlValue);
        this.isFormattedValue = true;
    }

    /**
     * Evento `keydown`.
     * Salva informações da seleção do valor e verifica se é adição/subtração.
     *
     * @memberof NDDFormNumericComponent
     */
    public keydown(event: KeyboardEvent, input: HTMLInputElement): void {
        this.select(input);
        this.changeValueByArrowKey(event);
    }

    /**
     * Evento `select`.
     * Salva informações da seleção do valor.
     *
     * @memberof NDDFormNumericComponent
     */
    public select(input: HTMLInputElement): void {
        const selectionStart: number = input.selectionStart;
        const selectionEnd: number = input.selectionEnd;

        this.inputSelection = {
            start: selectionStart,
            end: selectionEnd,
            text: this.controlValueString.substring(selectionStart, selectionEnd),
        };
    }

    /**
     * Evento `drop`.
     * Valida valor inserido.
     *
     * @memberof NDDFormNumericComponent
     */
    public dropValue(event: any): void {
        event.dataTransfer.items[0]
            .getAsString((value: string) => { this.fixDroppedValue(value); });
    }

    /**
     * Evento `paste`.
     * Valida valor inserido.
     *
     * @memberof NDDFormNumericComponent
     */
    public pasteValue(event: any): void {
        event.clipboardData.items[0]
            .getAsString((value: string) => { this.fixDroppedValue(value); });
    }

    /**
     * Gera o valor para o campo input (edição ou exibição).
     *
     * @memberof NDDFormNumericComponent
     */
    public getFixedHtmlInputValue(): any {
        return (this.controlValueAux || this.controlValueAux === 0)
            && this.isFormattedValue
            && this.controlFormat
            ? this.intl.formatNumber(this.controlValueAux, this.controlFormat.format, this.controlFormat.culture)
            : this.localeValue;
    }

    /**
     * Adição do valor, conforme `controlStep`.
     *
     * @memberof NDDFormNumericComponent
     */
    public increaseValue(): void {
        if (isNaN(parseFloat(this.controlValueAux))) {
            this.controlValueAux = 0;
        }

        // Verifica se valor é diferente do máximo inteiro permitido.
        if (this.controlValueAux !== this.controlIntegerMax) {
            // Soma valor, de acordo com máximo inteiro permitido.
            if (this.controlValueAux < this.controlIntegerMin) {
                this.controlValueAux = this.controlIntegerMin;
            } else if (this.controlValueAux < this.controlIntegerMax) {
                const newValue: number = +(this.controlValueAux + this.controlStep).toFixed(this.toFixedValue);

                this.controlValueAux = newValue > this.controlIntegerMax ? this.controlIntegerMax : newValue;
            } else {
                this.controlValueAux = this.controlIntegerMax;
            }
        }

        this.afterSumValue();
    }

    /**
     * Subtração do valor, conforme `controlStep`.
     *
     * @memberof NDDFormNumericComponent
     */
    public decreaseValue(): void {
        if (isNaN(parseFloat(this.controlValueAux))) {
            this.controlValueAux = 0;
        }

        // Verifica se valor é diferente do mínimo inteiro permitido.
        if (this.controlValueAux !== this.controlIntegerMin) {
            // Soma valor, de acordo com mínimo inteiro permitido.
            if (this.controlValueAux > this.controlIntegerMax) {
                this.controlValueAux = this.controlIntegerMax;
            } else if (this.controlValueAux > this.controlIntegerMin) {
                const newValue: number = +(this.controlValueAux - this.controlStep).toFixed(this.toFixedValue);

                this.controlValueAux = newValue < this.controlIntegerMin ? this.controlIntegerMin : newValue;
            } else {
                this.controlValueAux = this.controlIntegerMin;
            }
        }

        this.afterSumValue();
    }

    /**
     * Evento `keypress`.
     * Valida tecla digitada.
     *
     * @returns {boolean} Validação.
     * @memberof NDDFormNumericComponent
     */
    public validateChar(event: KeyboardEvent): boolean {
        /*
         * Verifica se é tecla que emitirá valor ou tecla de ação.
         * Validação necessária para corrigir comportamento do Firefox.
         */
        // tslint:disable-next-line:deprecation
        if ((event.key && event.key.length > 1) || (event.char && event.char.length > 1)) {
            return true;
        }

        // Verifica se o valor atual possui menos caracteres que o máximo permitido, ou se há texto selecionado.
        if (this.controlValueString.replace('.', '').replace('-', '').length < NDDFormNumericComponent.MAX_DIGITS_ALLOWED
            || this.inputSelection.start !== this.inputSelection.end) {
            // tslint:disable-next-line:deprecation
            if (this.validateCharNumeric(event.charCode)) {
                return true;
            }
        }

        if (this.validateCharDash(event.key)) {
            return true;
        }

        if (this.controlDecimalDigitsMax !== 0) {
            if (this.validateCharDecimalPoint(event.key)) {
                return true;
            }
        }

        return false;
    }

    /** Validação numérica. */
    private validateCharNumeric(charCode: number): boolean {
        if (this.control.value !== null) {
            return charCode >= NDDFormNumericComponent.CHAR_CODE_MIN_NUMERIC
                && charCode <= NDDFormNumericComponent.CHAR_CODE_MAX_NUMERIC
                && this.validateCharWithDash();
        }

        return charCode >= NDDFormNumericComponent.CHAR_CODE_MIN_NUMERIC
            && charCode <= NDDFormNumericComponent.CHAR_CODE_MAX_NUMERIC;
    }

    /** Validação para caractere negativo. */
    private validateCharDash(key: string): boolean {
        if (key === this.localeMinusSign && this.control.value === null) {
            return true;
        }

        return key === this.localeMinusSign
            && (this.inputSelection.start === 0 || this.inputSelection.end === 0)
            && (this.control.value === ''
                || this.control.value.toString().indexOf('-') === -1
                || this.inputSelection.text.indexOf('-') > -1);
    }

    /** Validação para caractere decimal. */
    private validateCharDecimalPoint(key: string): boolean {
        return key === this.localeDecimalChar
            && (this.control.value !== null
                && !isNaN(parseFloat(this.control.value))
                && this.inputSelection.text.length === 0
                && this.control.value.toString().indexOf(this.localeDecimalChar) === -1
                && this.control.value.toString().indexOf('.') === -1)
            && this.validateCharWithDash();
    }

    /** Validação para caractere decimal com caractere negativo. */
    private validateCharWithDash(): boolean {
        return (this.control.value !== null && (this.control.value === undefined || this.control.value.toString().indexOf('-') === -1))
            || this.inputSelection.text.indexOf('-') > -1
            || this.inputSelection.start > 0;
    }

    /** Define valor para o controle após realizar soma. */
    private afterSumValue(): void {
        this.control.setValue(this.controlValueAux);
        this.control.markAsDirty();
    }

    /** Realiza soma de valores quando a tecla digitada for `'ArrowUp'` ou `'ArrowDown'`. */
    private changeValueByArrowKey(event: KeyboardEvent): void {
        if (this.controlReadonly) { return; }

        if (event.code === 'ArrowUp') {
            event.preventDefault();

            this.increaseValue();
        }
        if (event.code === 'ArrowDown') {
            event.preventDefault();

            this.decreaseValue();
        }
    }

    /** Ajusta valor do controle após eventos `drop` e `paste`. */
    private fixDroppedValue(value: string): void {
        // TODO: Melhorar validação para aceitar sinal negativo e pontuação decimal.
        if (isNaN(+value)) {
            this.window.setTimeout(() => {
                this.control.setValue(this.localeValue);
            });
        }
    }

    /** Monitora a alteração do valor do controle. */
    private handleValueChanges(value: any): void {
        value = value === undefined || value === null ? '' : value;

        // Guarda quantidade de zeros do final do número, quando decimal.
        // Pois, `controlValueAux` ignora estes, por salvar como valor numérico e não string (ignora 0 no final de decimais; comportamento padrão JS).
        this.finalZeros = '';
        if (value && value.toString().length > 1 && value.toString().indexOf(this.localeDecimalChar) > -1) {
            const hasFinalZeros: RegExpExecArray = /0+$/.exec(value);
            this.finalZeros = hasFinalZeros ? hasFinalZeros[0] : '';
        }

        const oldValue: any = value;
        // Altera caracteres locais para caracteres válidos para o tipo `number`.
        value = value.toString().replace(this.localeDecimalChar, '.').replace(this.localeMinusSign, '-');

        // Verifica se é número.
        if (!isNaN(parseFloat(value))) {
            let formattedValue: string;

            this.controlValueAux = +value;
            this.controlValue = +value;
            value = +value;

            formattedValue = this.intl.formatNumber(value, this.controlFormat.format, this.controlFormat.culture);

            this.controlValueChange.emit(this.controlValue);
            this.onControlFormattedValueChange.emit(<INDDFormNumericFormattedValue>{ formattedValue, value: +value });

            // Corrige valor de exibição.
            this.input.nativeElement.value = this.getFixedHtmlInputValue();

            return;
        }

        // Verifica se contém apenas caracteres locais.
        switch (oldValue.toString()) {
            case this.localeMinusSign:
            case this.localeDecimalChar:
            case this.localeMinusSign + this.localeDecimalChar:
                this.controlValueAux = oldValue.toString();
                break;

            default:
                this.controlValueAux = null;
                break;
        }

        this.controlValue = null;
        this.controlValueChange.emit(this.controlValue);
        this.onControlFormattedValueChange.emit(<INDDFormNumericFormattedValue>{ formattedValue: null, value: null });
    }
}
